// feat_407 — detalle de una consulta: hilo, adjuntos, empresa/ticket, borrador de la IA y acciones.
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box, Button, Chip, CircularProgress, Collapse, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, IconButton, TextField, Typography, makeStyles, useMediaQuery, useTheme,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { toast } from "react-toastify";
import api from "../../services/api";
import whatsBackground from "../../assets/wa-background.webp";
import {
  AUTHORSHIP_LABELS, AuthImage, CopyId, REASON_LABELS, StatusChip, TYPE_LABELS,
  errorText, fmtConfidence, fmtDateTime, renderWhatsApp,
} from "./shared";
import { ReviewButtons } from "./RequestCard";
import ChecklistDialog from "./ChecklistDialog";
import ManualDialog, { GAP_STATUS_LABELS, DELIVERY_LABELS } from "./ManualDialog";

const useStyles = makeStyles((theme) => ({
  section: { marginTop: theme.spacing(2) },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: theme.spacing(1) },
  box: { border: "1px solid #e3e8eb", borderRadius: 6, padding: theme.spacing(1, 1.5) },
  thread: {
    backgroundImage: `url(${whatsBackground})`,
    backgroundColor: theme.palette.type === "dark" ? "#0b141a" : "#efeae2",
    borderRadius: 6,
    padding: theme.spacing(1.5),
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  bubble: {
    maxWidth: "85%",
    padding: "6px 9px 4px",
    borderRadius: 7.5,
    boxShadow: "0 1px .5px rgba(11,20,26,.13)",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    fontSize: 14,
  },
  client: { alignSelf: "flex-start", background: theme.palette.type === "dark" ? "#202c33" : "#fff" },
  ours: { alignSelf: "flex-end", background: theme.palette.type === "dark" ? "#005c4b" : "#d9fdd3" },
  ask: { alignSelf: "flex-end", background: theme.palette.type === "dark" ? "#3b3320" : "#fff8e1" },
  note: { alignSelf: "center", background: theme.palette.type === "dark" ? "#33302a" : "#fff3cd", border: "1px dashed #c9a227", maxWidth: "95%" },
  meta: { fontSize: 11, color: theme.palette.type === "dark" ? "#aebac1" : "#667781", marginTop: 2 },
  draft: { border: "2px solid #00897b", borderRadius: 6, padding: theme.spacing(1.5), background: theme.palette.type === "dark" ? "#10302c" : "#f1faf9" },
  pre: { whiteSpace: "pre-wrap", wordBreak: "break-word", fontFamily: "monospace", fontSize: 12, background: theme.palette.type === "dark" ? "#1e1e1e" : "#f5f5f5", padding: 8, borderRadius: 4, maxHeight: 260, overflow: "auto" },
  actions: { display: "flex", flexWrap: "wrap", gap: 8, marginTop: theme.spacing(1) },
  editorRow: { display: "grid", gridTemplateColumns: "1fr", gap: theme.spacing(1.5), [theme.breakpoints.up("md")]: { gridTemplateColumns: "1fr 1fr" } },
  preview: {
    backgroundImage: `url(${whatsBackground})`,
    backgroundColor: theme.palette.type === "dark" ? "#0b141a" : "#efeae2",
    borderRadius: 6,
    padding: theme.spacing(1.5),
    minHeight: 120,
    display: "flex",
    flexDirection: "column",
  },
}));

const ROLE_LABEL = {
  cliente: "Cliente",
  ia_repregunta: "Pedimos más datos",
  respuesta: "Nuestra respuesta",
  nota_interna: "Nota interna (solo equipo)",
};

/** Editor con vista previa en burbuja de WhatsApp. mode: edit | manual | ask */
const Editor = ({ mode, initial, onCancel, onSubmit, busy }) => {
  const classes = useStyles();
  const [text, setText] = useState(initial || "");
  const title = mode === "ask" ? "Pedir más datos al cliente" : mode === "edit" ? "Editar y enviar" : "Responder a mano";
  const max = mode === "ask" ? 2000 : 8000;
  return (
    <Box className={classes.section} data-support-editor={mode}>
      <Typography variant="subtitle2">{title}</Typography>
      <div className={classes.editorRow}>
        <TextField
          multiline
          minRows={6}
          maxRows={18}
          variant="outlined"
          fullWidth
          value={text}
          onChange={(e) => setText(e.target.value)}
          inputProps={{ maxLength: max, "data-support-editor-input": "1" }}
          helperText={`${text.length}/${max} · *negrita* _cursiva_ ~tachado~`}
          autoFocus
        />
        <div className={classes.preview} aria-label="Vista previa">
          <Typography variant="caption" style={{ color: "#667781", marginBottom: 4 }}>Vista previa (así lo ve el cliente)</Typography>
          <div className={`${classes.bubble} ${mode === "ask" ? classes.ask : classes.ours}`} data-support-preview>
            {text.trim() ? renderWhatsApp(text) : <span style={{ color: "#999" }}>…</span>}
          </div>
        </div>
      </div>
      <div className={classes.actions}>
        <Button variant="contained" color="primary" disabled={busy || !text.trim()} onClick={() => onSubmit(text)} data-support-send>
          {mode === "ask" ? "Enviar pregunta" : "Enviar al cliente"}
        </Button>
        <Button onClick={onCancel} disabled={busy}>Cancelar</Button>
      </div>
    </Box>
  );
};

const RequestDetail = ({ requestId, onClose, onChanged, canChecklist }) => {
  const classes = useStyles();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [editor, setEditor] = useState(null); // edit | manual | ask
  const [zoom, setZoom] = useState(null);
  const [showDiag, setShowDiag] = useState(false);
  const [showAudit, setShowAudit] = useState(false);

  // onClose llega inline del padre (referencia nueva por render): va por ref para que `load`
  // dependa SOLO del id (si no, el effect de abajo refetchearía en cada render).
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get(`/superadmin/support/requests/${requestId}`);
      setData(d);
    } catch (err) {
      toast.error(errorText(err));
      onCloseRef.current();
    } finally {
      setLoading(false);
    }
  }, [requestId]);

  useEffect(() => {
    setLoading(true);
    setEditor(null);
    load();
  }, [load]);

  const act = async (path, body, okMsg) => {
    setBusy(true);
    try {
      await api.post(`/superadmin/support/requests/${requestId}/${path}`, body || {});
      toast.success(okMsg);
      setEditor(null);
      await load();
      onChanged && onChanged();
    } catch (err) {
      toast.error(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  // feat_408 — "Pasar a checklist" / "Crear manual" (siempre con confirmación).
  const [checklistOpen, setChecklistOpen] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const closeChecklist = useCallback(() => setChecklistOpen(false), []);
  const closeManual = useCallback(() => setManualOpen(false), []);
  const afterBridge = useCallback(async () => { await load(); onChanged && onChanged(); }, [load, onChanged]);

  const r = data?.request;
  const d = data?.draft;
  const draftUsable = d && d.answer && !["enviado", "descartado"].includes(d.status);
  const closed = r?.status === "cerrada";
  const attByMsg = {};
  (data?.attachments || []).forEach((a) => {
    const k = a.messageId || "req";
    (attByMsg[k] = attByMsg[k] || []).push(a);
  });
  const firstClientMsg = (data?.messages || []).find((m) => m.role === "cliente");

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md" fullScreen={fullScreen} scroll="paper" data-support-detail={requestId}>
      <DialogTitle disableTypography style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", paddingRight: 48 }}>
        {r ? <CopyId id={r.id} /> : <Typography>#{requestId}</Typography>}
        {data?.company && <Chip size="small" variant="outlined" label={`#${data.company.id} ${data.company.name}`} />}
        {r && <StatusChip status={r.status} />}
        {r?.late && <Chip size="small" label="Más de 8 h" style={{ background: "#d32f2f", color: "#fff" }} />}
        {r?.noManual && <Chip size="small" label="Sin manual" style={{ background: "#ff6f00", color: "#fff" }} />}
        <IconButton aria-label="Cerrar" onClick={onClose} style={{ position: "absolute", right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading || !data ? (
          <Box style={{ display: "flex", justifyContent: "center", padding: 32 }}><CircularProgress /></Box>
        ) : (
          <>
            <div className={classes.grid}>
              <div className={classes.box}>
                <Typography variant="caption" color="textSecondary">Empresa</Typography>
                <Typography variant="body2"><b>#{data.company?.id} {data.company?.name}</b></Typography>
                <Typography variant="caption" component="div">
                  Marca: {data.company?.brand || "—"} · Plan: {data.company?.plan || "—"}
                  {data.company?.active === false ? " · INACTIVA" : ""}
                  {data.company?.operationStatus && data.company.operationStatus !== "active" ? ` · ${data.company.operationStatus}` : ""}
                </Typography>
              </div>
              <div className={classes.box}>
                <Typography variant="caption" color="textSecondary">Consultó</Typography>
                <Typography variant="body2">{data.asker?.name || "—"}</Typography>
                <Typography variant="caption" component="div">{data.asker?.email || ""} · {fmtDateTime(r.createdAt)} · idioma {r.language || "—"}</Typography>
              </div>
              <div className={classes.box}>
                <Typography variant="caption" color="textSecondary">Ticket / conexión</Typography>
                <Typography variant="body2">
                  {data.ticket ? `Ticket ${data.ticket.id} (${data.ticket.status})` : r.ticketId ? `Ticket ${r.ticketId} (no encontrado)` : "Sin ticket"}
                </Typography>
                <Typography variant="caption" component="div">
                  {data.ticket?.contact ? `Contacto: ${data.ticket.contact.name || data.ticket.contact.number}` : ""}
                  {data.whatsapp ? ` · Conexión: ${data.whatsapp.name} (${data.whatsapp.provider || "—"}, ${data.whatsapp.status})` : ""}
                </Typography>
              </div>
              <div className={classes.box}>
                <Typography variant="caption" color="textSecondary">Clasificación</Typography>
                <Typography variant="body2">{TYPE_LABELS[r.type] || r.type}{r.clarificationCount ? ` · ${r.clarificationCount} repregunta(s)` : ""}</Typography>
                {data.gap && (
                  <Typography variant="caption" component="div" data-detail-gap={data.gap.id}>
                    Brecha #{data.gap.id}: {data.gap.topic} ({GAP_STATUS_LABELS[data.gap.status] || data.gap.status}, {data.gap.affectedCount} consulta/s)
                    {data.gap.status === "manual_en_cola" ? ` · ${data.gap.promptWrittenAt ? DELIVERY_LABELS.encolado : DELIVERY_LABELS.pendiente_servidor}` : ""}
                  </Typography>
                )}
                {data.checklistTask ? (
                  <Typography variant="caption" component="div" data-detail-checklist={data.checklistTask.id}>
                    Checklist: tarea #{data.checklistTask.id} «{data.checklistTask.title}» · {data.checklistTask.status === "done" ? "terminada" : data.checklistTask.status === "in_progress" ? `en curso (${data.checklistTask.progressPct}%)` : "pendiente"}
                    {r.status === "resuelta_checklist" ? " — lista para avisar al cliente (respondé y aprobá vos)" : ""}
                  </Typography>
                ) : r.checklistTaskId ? (
                  <Typography variant="caption" component="div">Checklist: la tarea #{r.checklistTaskId} se borró.</Typography>
                ) : null}
              </div>
            </div>

            <Typography variant="subtitle2" className={classes.section}>Conversación</Typography>
            <div className={classes.thread} data-support-thread>
              {data.messages.map((m) => {
                const cls = m.role === "cliente" ? classes.client : m.role === "nota_interna" ? classes.note : m.role === "ia_repregunta" ? classes.ask : classes.ours;
                const atts = [...(attByMsg[m.id] || []), ...(m === firstClientMsg ? attByMsg.req || [] : [])];
                return (
                  <div key={m.id} className={`${classes.bubble} ${cls}`} data-support-msg={m.role}>
                    <Typography variant="caption" style={{ fontWeight: 700, display: "block" }}>{ROLE_LABEL[m.role] || m.role}</Typography>
                    {m.role === "cliente" ? m.body : renderWhatsApp(m.body)}
                    {atts.length > 0 && (
                      <Box style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 6 }}>
                        {atts.map((a) => <AuthImage key={a.id} id={a.id} onOpen={setZoom} />)}
                      </Box>
                    )}
                    <div className={classes.meta}>
                      {fmtDateTime(m.createdAt)}
                      {m.role === "respuesta" && ` · ${AUTHORSHIP_LABELS[m.authorship] || m.authorship}${m.userName ? ` · ${m.userName}` : ""}${m.actor ? ` (${m.actor})` : ""}${m.aiModel ? ` · ${m.aiProvider}/${m.aiModel}` : ""}${m.confidence != null ? ` · ${fmtConfidence(m.confidence)}` : ""}`}
                      {m.role === "ia_repregunta" && (m.userName ? ` · ${m.userName}` : " · IA")}
                      {m.role === "nota_interna" && m.actor ? ` · ${m.actor}` : ""}
                    </div>
                    {m.role === "respuesta" && m.authorship === "ia_editada" && m.aiOriginalText && (
                      <details style={{ marginTop: 4 }}>
                        <summary style={{ fontSize: 12, cursor: "pointer" }}>Texto original de la IA</summary>
                        <div className={classes.pre}>{m.aiOriginalText}</div>
                      </details>
                    )}
                    {m.role === "respuesta" && (
                      <Box style={{ marginTop: 4 }}><ReviewButtons answer={m} onDone={load} /></Box>
                    )}
                  </div>
                );
              })}
            </div>

            {d && (
              <Box className={`${classes.section} ${classes.draft}`} data-support-draft={d.id}>
                <Box style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
                  <Typography variant="subtitle2">Borrador de la IA</Typography>
                  <Chip size="small" label={d.status} />
                  {d.decisionReason && <Chip size="small" variant="outlined" label={REASON_LABELS[d.decisionReason] || d.decisionReason} />}
                  {d.confidence != null && <Chip size="small" variant="outlined" label={`Confianza ${fmtConfidence(d.confidence)}`} />}
                  {d.topic && <Chip size="small" variant="outlined" label={`Tema: ${d.topic}`} />}
                  {d.border && <Chip size="small" label={`Borde: ${d.borderReason || ""}`} style={{ background: "#b71c1c", color: "#fff" }} />}
                </Box>
                <Typography variant="caption" color="textSecondary" component="div">
                  {d.provider || "sin IA"}{d.model ? ` · ${d.model}` : ""} · vuelta {d.turn}{d.generation ? `.${d.generation}` : ""} · {d.tokensIn || 0}/{d.tokensOut || 0} tokens · US$ {Number(d.costUsd || 0).toFixed(4)}{d.error ? ` · error: ${d.error}` : ""}
                </Typography>
                {d.answer ? (
                  <div className={`${classes.bubble} ${classes.ours}`} style={{ marginTop: 8, maxWidth: "100%" }}>{renderWhatsApp(d.answer)}</div>
                ) : (
                  <Typography variant="body2" style={{ marginTop: 8 }}>La IA no dejó texto para enviar.</Typography>
                )}
                {(d.sources || []).length > 0 && (
                  <Box style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 }}>
                    {d.sources.map((s, i) => <Chip key={i} size="small" variant="outlined" label={`${s.archivo}${s.seccion ? ` § ${s.seccion}` : ""}`} />)}
                  </Box>
                )}
                {(d.diagnosis || d.proposedPrompt) && (
                  <>
                    <Button size="small" onClick={() => setShowDiag((v) => !v)} style={{ marginTop: 6 }}>
                      {showDiag ? "Ocultar" : "Ver"} diagnóstico y prompt propuesto (solo equipo)
                    </Button>
                    <Collapse in={showDiag}>
                      {d.diagnosis && (<><Typography variant="caption">Diagnóstico</Typography><div className={classes.pre}>{d.diagnosis}</div></>)}
                      {d.proposedPrompt && (<><Typography variant="caption">Prompt propuesto</Typography><div className={classes.pre}>{d.proposedPrompt}</div></>)}
                    </Collapse>
                  </>
                )}
              </Box>
            )}

            {editor ? (
              <Editor
                key={editor}
                mode={editor}
                busy={busy}
                initial={editor === "edit" ? d?.answer : ""}
                onCancel={() => setEditor(null)}
                onSubmit={(text) => {
                  if (editor === "ask") return act("ask-more", { text }, "Pregunta enviada al cliente.");
                  if (editor === "edit") return act("edit-send", { body: text, draftId: d?.id }, "Respuesta enviada al cliente.");
                  return act("reply", { body: text }, "Respuesta enviada al cliente.");
                }}
              />
            ) : (
              <div className={classes.actions} style={{ marginTop: 16 }}>
                {!closed && draftUsable && (
                  <Button variant="contained" style={{ background: "#00a884", color: "#fff" }} disabled={busy}
                    onClick={() => window.confirm("¿Enviar el borrador de la IA tal cual al cliente?") && act("approve", { draftId: d.id }, "Respuesta enviada al cliente.")}
                    data-support-approve>
                    Aprobar y enviar
                  </Button>
                )}
                {!closed && draftUsable && <Button variant="outlined" color="primary" disabled={busy} onClick={() => setEditor("edit")} data-support-edit>Editar y enviar</Button>}
                {!closed && <Button variant="outlined" color="primary" disabled={busy} onClick={() => setEditor("manual")} data-support-manual>Responder a mano</Button>}
                {!closed && <Button variant="outlined" disabled={busy} onClick={() => setEditor("ask")} data-support-ask>Pedir más datos</Button>}
                {!closed && draftUsable && (
                  <Button variant="outlined" disabled={busy} style={{ color: "#d32f2f", borderColor: "#d32f2f" }}
                    onClick={() => window.confirm("¿Descartar el borrador? No se envía nada al cliente.") && act("discard", {}, "Borrador descartado.")}
                    data-support-discard>
                    Descartar
                  </Button>
                )}
                {!closed && canChecklist && (
                  <Button variant="outlined" disabled={busy} onClick={() => setChecklistOpen(true)} data-support-to-checklist>
                    {data.checklistTask ? "Ver tarea de checklist" : "Pasar a checklist"}
                  </Button>
                )}
                {data.gap && (
                  <Button variant="outlined" disabled={busy} style={{ color: "#e65100", borderColor: "#e65100" }} onClick={() => setManualOpen(true)} data-support-create-manual>
                    {data.gap.status === "abierta" ? "Crear manual" : "Ver prompt de manual"}
                  </Button>
                )}
                {!closed ? (
                  <Button disabled={busy} onClick={() => window.confirm("¿Cerrar la consulta? No se avisa nada al cliente.") && act("close", {}, "Consulta cerrada.")} data-support-close>Cerrar consulta</Button>
                ) : (
                  <Button variant="outlined" disabled={busy} onClick={() => act("reopen", {}, "Consulta reabierta.")} data-support-reopen>Reabrir</Button>
                )}
              </div>
            )}

            <Divider className={classes.section} />
            <Button size="small" onClick={() => setShowAudit((v) => !v)} style={{ marginTop: 8 }}>
              {showAudit ? "Ocultar" : "Ver"} registro de acciones ({data.actions.length})
            </Button>
            <Collapse in={showAudit}>
              {data.actions.length === 0 ? (
                <Typography variant="caption" color="textSecondary">Sin acciones del equipo todavía.</Typography>
              ) : (
                data.actions.map((a) => (
                  <Typography key={a.id} variant="caption" component="div">
                    {fmtDateTime(a.createdAt)} · {a.userName || `user ${a.userId}`}{a.actor ? ` (${a.actor})` : ""} · <b>{a.action}</b>
                  </Typography>
                ))
              )}
            </Collapse>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
      {checklistOpen && <ChecklistDialog requestId={requestId} open onClose={closeChecklist} onDone={afterBridge} />}
      {manualOpen && data?.gap && <ManualDialog gapId={data.gap.id} open onClose={closeManual} onDone={afterBridge} />}
      <Dialog open={!!zoom} onClose={() => setZoom(null)} maxWidth="lg">
        {zoom && <img src={zoom} alt="Adjunto" style={{ maxWidth: "100%", display: "block" }} onClick={() => setZoom(null)} />}
      </Dialog>
    </Dialog>
  );
};

export default RequestDetail;
